import { useCallback } from "react";
import { useRoutes } from "react-router-dom";
import { useRouter } from "./hooks/use-router";
import { Helmet } from "react-helmet-async";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RTL } from "./components/rtl";
import { SplashScreen } from "./components/splash-screen";
import { SettingsDrawer } from "./components/settings/settings-drawer";
import { Toaster } from "./components/toaster";
import { toast } from "react-hot-toast";
import {
  SettingsConsumer,
  SettingsProvider,
} from "./contexts/settings-context";
import { AuthConsumer, AuthProvider } from "./contexts/auth/jwt-context";
import useIdleTimer from "./hooks/use-idle-timer";
import { useNprogress } from "./hooks/use-nprogress";
import { useAuth } from "./hooks/use-auth";
import { routes } from "./routes";
import { paths } from "./paths";
import { logoutTimerConfig } from "./config";
import { createTheme } from "./theme";
import { ko } from "date-fns/locale";
import { koKR } from "@mui/material/locale";
import "simplebar-react/dist/simplebar.min.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      suspense: true,
    },
  },
});

export const App = () => {
  useNprogress();

  const element = useRoutes(routes);
  const router = useRouter();
  const { user, signOut } = useAuth();

  const handleTimeout = useCallback(async () => {
    await signOut(user.id, user.username);

    router.push(paths.auth.login);

    toast.error("장시간 입력이 없어 자동으로 로그아웃되었습니다.", {
      id: "logout-timer",
      duration: Infinity,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signOut, user, router]);

  useIdleTimer({
    onTimeout: handleTimeout,
    timeoutSeconds: logoutTimerConfig.TIMEOUT_SEC,
    excludePages: logoutTimerConfig.EXCLUDE_PAGES,
  });

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={ko}
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AuthConsumer>
            {(auth) => (
              <SettingsProvider>
                <SettingsConsumer>
                  {(settings) => {
                    // Prevent theme flicker when restoring custom settings from browser storage
                    if (!settings.isInitialized) {
                      // return null;
                    }

                    const theme = createTheme({
                      colorPreset: settings.colorPreset,
                      contrast: settings.contrast,
                      direction: settings.direction,
                      paletteMode: settings.paletteMode,
                      responsiveFontSizes: settings.responsiveFontSizes,
                      locale: koKR,
                    });

                    // Prevent guards from redirecting
                    const showSlashScreen = !auth.isInitialized;

                    return (
                      <ThemeProvider theme={theme}>
                        <Helmet>
                          <meta
                            name="color-scheme"
                            content={settings.paletteMode}
                          />
                          <meta
                            name="theme-color"
                            content={theme.palette.neutral[900]}
                          />
                        </Helmet>
                        <RTL direction={settings.direction}>
                          <CssBaseline />
                          {showSlashScreen ? (
                            <SplashScreen />
                          ) : (
                            <>
                              {element}
                              <SettingsDrawer
                                canReset={settings.isCustom}
                                onClose={settings.handleDrawerClose}
                                onReset={settings.handleReset}
                                onUpdate={settings.handleUpdate}
                                open={settings.openDrawer}
                                values={{
                                  colorPreset: settings.colorPreset,
                                  contrast: settings.contrast,
                                  direction: settings.direction,
                                  paletteMode: settings.paletteMode,
                                  responsiveFontSizes:
                                    settings.responsiveFontSizes,
                                  stretch: settings.stretch,
                                  layout: settings.layout,
                                  navColor: settings.navColor,
                                }}
                              />
                            </>
                          )}
                          <Toaster />
                        </RTL>
                      </ThemeProvider>
                    );
                  }}
                </SettingsConsumer>
              </SettingsProvider>
            )}
          </AuthConsumer>
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </LocalizationProvider>
  );
};
